// Perspective transformation utility for mapping robot coordinates to map display coordinates
// Based on 4-point perspective transformation using numeric.js

import numeric from 'numeric';
import { CONFIG } from '../config';

/**
 * Compute perspective transformation matrix from source and destination points
 * @param {Array} srcPts - Array of 4 source points {x, y}
 * @param {Array} dstPts - Array of 4 destination points {x, y}
 * @returns {Array} 3x3 transformation matrix H
 */
export function computePerspectiveTransform(srcPts, dstPts) {
  const A = [];

  for (let i = 0; i < 4; i++) {
    const sx = srcPts[i].x;
    const sy = srcPts[i].y;
    const dx = dstPts[i].x;
    const dy = dstPts[i].y;

    A.push([-sx, -sy, -1, 0, 0, 0, sx * dx, sy * dx, dx]);
    A.push([0, 0, 0, -sx, -sy, -1, sx * dy, sy * dy, dy]);
  }

  // Compute A^T * A
  const AT = numeric.transpose(A);
  const ATA = numeric.dot(AT, A);
  
  // Get eigenvalues and eigenvectors
  const eig = numeric.eig(ATA);
  const eigenvectors = eig.E.x;
  const eigenvalues = eig.lambda.x;
  
  // Find index of smallest eigenvalue
  const smallestEigenIndex = eigenvalues.indexOf(Math.min(...eigenvalues));
  
  // Get corresponding eigenvector (solution to Ah = 0)
  const h = eigenvectors.map(row => row[smallestEigenIndex]);

  // Convert h (flat) to 3x3 matrix H
  const H = [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], h[8]]
  ];

  return H;
}

/**
 * Apply perspective transformation to a point
 * @param {Object} pt - Point {x, y} (robot coordinates)
 * @param {Array} H - 3x3 transformation matrix
 * @returns {Object} Transformed point {x, y}
 */
export function applyPerspectiveTransform(pt, H) {
  // 로봇 좌표를 변환된 srcPts 좌표계로 변환
  const transformedPt = CONFIG.transformRobotPoint(pt);
  const x = transformedPt.x;
  const y = transformedPt.y;

  const denom = H[2][0] * x + H[2][1] * y + H[2][2];
  if (Math.abs(denom) < 0.000001) {
    console.warn("Invalid perspective matrix or point at infinity");
    return { x: 0, y: 0 };
  }

  const newX = (H[0][0] * x + H[0][1] * y + H[0][2]) / denom;
  const newY = (H[1][0] * x + H[1][1] * y + H[1][2]) / denom;

  return { x: newX, y: newY };
}

/**
 * Initialize perspective transformation from config
 * @param {Object} config - Config object with srcPts and dstPts
 * @returns {Array} Transformation matrix H
 */
export function initializePerspectiveTransform(config) {
  return computePerspectiveTransform(config.srcPts, config.dstPts);
}
